import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { paisa, type Money } from '@fleet/shared';
import {
  useCreateRateCardsMutation,
  useRateCardQuery,
  useUpdateRateCardMutation,
  useVehiclesQuery,
} from '@/api/endpoints';
import type { RateType, TripArea, VehicleCategory } from '@/api/types';
import { Button, LinkButton } from '@/design/Button';
import { ErrorState } from '@/design/EmptyState';
import {
  Checkbox,
  Field,
  FieldRow,
  MoneyInput,
  NumberInput,
  SelectInput,
} from '@/design/Field';
import { Panel, PanelBody } from '@/design/Panel';
import { readApiError } from '@/lib/apiError';
import {
  CATEGORY_LABELS,
  RATE_TYPE_LABELS,
  TRIP_AREA_LABELS,
  distinctVehicleNames,
  optionsFrom,
} from '../fleet/labels';

/**
 * A rate card.
 *
 * The pair that causes trouble is included kilometres and the charge beyond
 * them: set the first without the second and every extra kilometre is free, and
 * nobody finds out for a month. So the form says what zero means, and the server
 * refuses the combination outright.
 */
export function RateCardFormPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== undefined && id !== 'new';
  const navigate = useNavigate();

  const existing = useRateCardQuery(id ?? '', { skip: !isEditing });
  const vehicles = useVehiclesQuery();
  const [createRateCards, creatingMany] = useCreateRateCardsMutation();
  const [updateRateCard, updating] = useUpdateRateCardMutation();

  /**
   * The four prices a category actually has, entered together.
   *
   * A rental quotes inside the valley and outside it, with a driver and
   * self-drive. Those are four rate cards, and until now they were four visits
   * to this form re-typing the same category, vehicle and kilometres — so in
   * practice two of the four never got entered and the quote engine fell back
   * to whichever card did exist.
   *
   * Only when adding. Editing is one row, and turning that into a grid would
   * make "save" mean something different depending on how you arrived.
   *
   * Keyed by area and driver; an amount left at zero is a combination this
   * business does not offer, and no card is created for it.
   */
  const [grid, setGrid] = useState<Record<string, Money>>({});
  const gridKey = (forArea: TripArea, driven: boolean): string =>
    `${forArea}:${driven ? 'driver' : 'self'}`;
  const gridValue = (forArea: TripArea, driven: boolean): Money =>
    grid[gridKey(forArea, driven)] ?? paisa(0);
  const filledIn = Object.values(grid).filter((amount) => amount > 0).length;

  const [category, setCategory] = useState<VehicleCategory>('sedan');
  const [vehicleId, setVehicleId] = useState('');
  const [withDriver, setWithDriver] = useState(true);
  const [area, setArea] = useState<TripArea>('inside_valley');
  const [rateType, setRateType] = useState<RateType>('per_day');
  const [baseAmount, setBaseAmount] = useState<Money>(paisa(0));
  const [includedKm, setIncludedKm] = useState<number | null>(0);
  const [perKmAmount, setPerKmAmount] = useState<Money>(paisa(0));
  const [minDays, setMinDays] = useState<number | null>(1);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const card = existing.data;
    if (card === undefined) return;
    setCategory(card.category);
    setVehicleId(card.vehicle ?? '');
    setWithDriver(card.with_driver !== false);
    setArea(card.area ?? 'inside_valley');
    setRateType(card.rate_type ?? 'per_day');
    setBaseAmount(paisa(card.base_amount));
    setIncludedKm(card.included_km ?? 0);
    setPerKmAmount(paisa(card.per_km_amount ?? 0));
    setMinDays(card.min_days ?? 1);
    setIsActive(card.is_active !== false);
  }, [existing.data]);

  const saving = creatingMany.isLoading || updating.isLoading;
  const [nothingEntered, setNothingEntered] = useState(false);
  const problems = readApiError(creatingMany.error ?? updating.error);
  const unlimited = (includedKm ?? 0) === 0;

  async function save(): Promise<void> {
    const body = {
      category,
      vehicle: vehicleId === '' ? null : vehicleId,
      with_driver: withDriver,
      area,
      rate_type: rateType,
      base_amount: baseAmount,
      included_km: includedKm ?? 0,
      per_km_amount: unlimited ? 0 : perKmAmount,
      min_days: minDays ?? 1,
      is_active: isActive,
    };

    if (isEditing) {
      await updateRateCard({ id, body }).unwrap();
      navigate('/rates');
      return;
    }

    // Everything the grid has a price for, in one atomic request. The shared
    // half of the card — category, vehicle, kilometres, minimum days — is the
    // same on all of them; only the area and the driver flag differ.
    const wanted = (['inside_valley', 'outside_valley'] as const).flatMap((forArea) =>
      [true, false]
        .filter((driven) => gridValue(forArea, driven) > 0)
        .map((driven) => ({
          ...body,
          area: forArea,
          with_driver: driven,
          base_amount: gridValue(forArea, driven),
        })),
    );

    if (wanted.length === 0) {
      setNothingEntered(true);
      return;
    }

    setNothingEntered(false);
    await createRateCards(wanted).unwrap();
    navigate('/rates');
  }

  if (isEditing && existing.isError) {
    return (
      <ErrorState
        headline="This rate card did not load"
        detail="Go back to rates and open it again."
        onRetry={() => void existing.refetch()}
        retryLabel="Load again"
      />
    );
  }

  const vehicleLabels = distinctVehicleNames(
    (vehicles.data?.results ?? []).map((vehicle) => ({
      id: vehicle.id,
      name: vehicle.display_name ?? `${vehicle.make} ${vehicle.model}`,
      plate: vehicle.plate,
    })),
  );
  const vehicleOptions = [
    { value: '', label: 'The whole category' },
    ...(vehicles.data?.results ?? []).map((vehicle) => ({
      value: vehicle.id,
      label: vehicleLabels.get(vehicle.id) ?? vehicle.display_name ?? vehicle.plate,
    })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">
        {isEditing ? 'Edit rate card' : 'Add rate card'}
      </h1>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="flex flex-col gap-4">
          <Panel title="What this card covers" current>
            <PanelBody>
              <div className="flex flex-col gap-4">
                <FieldRow>
                  <Field label="Category" required error={problems.fields['category']}>
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={category}
                        onChange={setCategory}
                        options={optionsFrom(CATEGORY_LABELS)}
                      />
                    )}
                  </Field>
                  <Field
                    label="One vehicle only"
                    error={problems.fields['vehicle']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={vehicleId}
                        onChange={setVehicleId}
                        options={vehicleOptions}
                      />
                    )}
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field label="Charged" error={problems.fields['rate_type']}>
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={rateType}
                        onChange={setRateType}
                        options={optionsFrom(RATE_TYPE_LABELS)}
                      />
                    )}
                  </Field>
                  <Field
                    label="Shortest booking"
                    error={problems.fields['min_days']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <NumberInput
                        id={fieldId}
                        describedBy={describedBy}
                        min={1}
                        unit="days"
                        value={minDays}
                        onChange={setMinDays}
                      />
                    )}
                  </Field>
                </FieldRow>

                {/* Editing is one card, so it keeps the two controls that
                    say which one. Adding is the grid below instead. */}
                {isEditing && (
                  <>
                    <Field label="Which trips" required error={problems.fields['area']}>
                      {({ id: fieldId, describedBy }) => (
                        <SelectInput
                          id={fieldId}
                          describedBy={describedBy}
                          value={area}
                          onChange={setArea}
                          options={optionsFrom(TRIP_AREA_LABELS)}
                        />
                      )}
                    </Field>

                    <Checkbox
                      label="With a driver"
                      checked={withDriver}
                      onChange={setWithDriver}
                    />
                  </>
                )}
              </div>
            </PanelBody>
          </Panel>

          <Panel
            title={isEditing ? 'The money' : 'The prices'}
            note={
              isEditing
                ? undefined
                : 'Fill in the ones you offer. Each price you enter becomes its own card; leave the rest empty.'
            }
          >
            <PanelBody>
              <div className="flex flex-col gap-4">
                {isEditing ? (
                  <Field label="Rate" required error={problems.fields['base_amount']}>
                    {({ id: fieldId, describedBy }) => (
                      <MoneyInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={baseAmount}
                        onChange={setBaseAmount}
                      />
                    )}
                  </Field>
                ) : (
                  /*
                    All four at once, which is what a rental actually has for a
                    category. Entering them one at a time meant two of the four
                    were usually never entered at all, and the quote engine
                    fell back to whichever card existed — so a valley rate
                    priced a trip to Pokhara.
                  */
                  <div className="flex flex-col gap-4">
                    {(['inside_valley', 'outside_valley'] as const).map((forArea) => (
                      <div key={forArea} className="flex flex-col gap-2">
                        <p className="text-small font-medium">{TRIP_AREA_LABELS[forArea]}</p>
                        <FieldRow>
                          {[true, false].map((driven) => (
                            <Field
                              key={String(driven)}
                              label={driven ? 'With a driver' : 'Self-drive'}
                            >
                              {({ id: fieldId, describedBy }) => (
                                <MoneyInput
                                  id={fieldId}
                                  describedBy={describedBy}
                                  value={gridValue(forArea, driven)}
                                  onChange={(amount) =>
                                    setGrid((was) => ({
                                      ...was,
                                      [gridKey(forArea, driven)]: amount,
                                    }))
                                  }
                                />
                              )}
                            </Field>
                          ))}
                        </FieldRow>
                      </div>
                    ))}

                    {nothingEntered && (
                      <p className="font-medium text-alert" role="alert">
                        Enter at least one price. An empty box means you do not offer that
                        combination.
                      </p>
                    )}
                  </div>
                )}

                <FieldRow>
                  <Field
                    label="Kilometres included (0 means unlimited)"
                    error={problems.fields['included_km']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <NumberInput
                        id={fieldId}
                        describedBy={describedBy}
                        unit="km"
                        value={includedKm}
                        onChange={setIncludedKm}
                      />
                    )}
                  </Field>
                  {/* Asked for only when it can apply. */}
                  {!unlimited && (
                    <Field
                      label="Each kilometre after that"
                      required
                      error={problems.fields['per_km_amount']}
                    >
                      {({ id: fieldId, describedBy }) => (
                        <MoneyInput
                          id={fieldId}
                          describedBy={describedBy}
                          value={perKmAmount}
                          onChange={setPerKmAmount}
                        />
                      )}
                    </Field>
                  )}
                </FieldRow>

                <Checkbox
                  label="In use"
                  checked={isActive}
                  onChange={setIsActive}
                />
              </div>
            </PanelBody>
          </Panel>

          {problems.detail !== undefined && (
            <p
              className="border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert"
              role="alert"
            >
              {problems.detail}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button intent="primary" size="touch" type="submit" disabled={saving}>
              {saving
                ? 'Saving'
                : isEditing
                  ? 'Save changes'
                  : filledIn <= 1
                    ? 'Add rate card'
                    : `Add ${String(filledIn)} rate cards`}
            </Button>
            <LinkButton to="/rates" size="touch">
              Cancel
            </LinkButton>
          </div>
        </div>
      </form>
    </div>
  );
}
